import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cloneDeep, set } from "lodash";
import { Accordion, AccordionDetails, AccordionSummary, Box, Modal } from "@mui/material";

import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import CtaComponent from "../../../components/reusable-ui/cta";

import { CTA_SIZE, CTA_TYPE, DATE_TIME_FORMATS } from "../../../data/constants";
import { RootState } from "../../../redux/store";
import "./style.scss";
import { useDispatch } from "react-redux";
import { fetchCommonData, getUserDetails } from "../../../redux/slices";
import moment, { Moment } from "moment";
import DateRangePicker from "../../../components/reusable-ui/date-range-picker";
import { METHODS } from "../../../data/api-method-constants";
import { ENDPOINT } from "../../../services/apiUrls";
import { toastSuccess } from "../../../utils/react-toaster";
import { enumerateDaysBetweenDates } from "../../../helpers/moment-helper";
import { UPLOAD_INSTRUCTION } from "./constant";

interface Props {
  isModeDownload?: boolean;
  onModalClose: any;
}

const ExpenseUploadModal: FC<Props> = (props: Props) => {
  const { onModalClose, isModeDownload } = props;
  const INITIAL_STATE_UPLOAD_DATA = {
    branchIds: [],
    fileData: "",
    dateRange: {
      startDate: "",
      endDate: "",
    },
  };

  const [payload, setPayload] = useState<any>(INITIAL_STATE_UPLOAD_DATA),
    [openUploadSection, setOpenUploadSection] = useState<boolean>(false),
    [userEmail, setUserEmail] = useState<string>("");
  const { businessBranchesDataMap, userData } = useSelector(
    (state: RootState) => ({
      businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
      userData: state.businessReducer.userData,
    })
  );
  const [isExpend,setIsExpend] = useState(true)
  const dispatch = useDispatch();
  const DROPDOWN_CONFIGS = {
    branchIds: {
      label: "Branch",
      optionsMap: businessBranchesDataMap,
      placeholder: "All Branches",
      uniqueKey: "branchId",
      dropdownHeading: "Branches",
      searchPlaceholder: "Search a branch",
      parentClass: "w-full mb-6 mt-32",
      key: "branchIds",
      getListItemText: (data: any) => data?.branchName,
      isMultiSelect: true,
    },
  };

  useEffect(() => {
    const callback = (data: any) => {
      if (data) {
        setUserEmail(data?.contactInfo?.email);
      }
    };
    if (userData?.businessUserId) {
      dispatch(
        getUserDetails({ callback, businessUserId: userData?.businessUserId })
      );
    }
  }, [userData?.businessUserId]);

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(payload);
    set(dataToUpdate, fieldToUpdate, value);
    setPayload(dataToUpdate);
  };

  const handleSendTemplate = () => {
    const callback = (response: any) => {
      if (!response?.code) {
        toastSuccess("Template Sent Successfully");
        onModalClose();
      }
    };
    let period: any = [];
    if (isModeDownload) {
      period = enumerateDaysBetweenDates(
        payload?.dateRange?.startDate,
        payload?.dateRange?.endDate,
        DATE_TIME_FORMATS.yearMonthDay
      );
    }
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: isModeDownload ? { period, branchIds: payload?.branchIds } : {},
        url: ENDPOINT.PNL_STATEMENT.sendExpenseUploadTemplate,
        callback,
      })
    );
  };

  const onUploadFileCtaClick = () => {
    const callback = (response: any) => {
      if (!response?.code) {
        toastSuccess("File Successfully Uploaded");
        onModalClose();
      }
    };
    const formData = new FormData();
    formData.append("file", payload?.fileData);
    formData.append("startDate", payload?.dateRange?.startDate);
    formData.append("endDate", payload?.dateRange?.endDate);
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: formData,
        url: ENDPOINT.PNL_STATEMENT.uploadExpenseData,
        callback,
      })
    );
  };

  /**
   * The dates more than 3 years will always be disabled
   * @param current
   */
  const disabledDate = (current: any) => {
    return (
      current &&
      (current >= moment().endOf("day") || moment().diff(current, "days") > 89)
    );
  };

  const onDateRangeSubmission = (data: {
    startDate: Moment;
    endDate: Moment;
  }) => {
    handleInputChange(data, "dateRange");
  };

  function renderCustomDropDown(dropdownConfig: {
    label?: string;
    optionsMap: any;
    placeholder?: string;
    getListItemText?: any;
    parentClass?: string;
    dropdownHeading?: string;
    searchPlaceholder?: string;
    uniqueKey: any;
    key: string;
    isMultiSelect?: boolean;
  }): any {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.label}
        optionsMap={dropdownConfig.optionsMap}
        isMultiSelect={dropdownConfig?.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={dropdownConfig.getListItemText}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={(value: any) => handleInputChange(value, dropdownConfig.key)}
        preSelectedValues={payload[dropdownConfig.key]}
        uniqueKey={dropdownConfig.uniqueKey}
        isMandatory={true}
      ></CustomDropdown>
    );
  }

  function renderFileChooser(): React.JSX.Element {
    return (
      <>
        <label
          className={`bg-[#397EF5] bg-opacity-10 mb-8 rounded-[8px] w-full p-8
          custom-file-upload flex items-center justify-center cursor-pointer
          border border-[#C5DBFF]
        `}
        >
          {payload?.fileData?.name ? (
            <div className="flex items-center justify-between w-full px-[20px]">
              <p
                className="text-16 text-blue font-semibold m-0 w-1/2 sm:w-3/4 line-clamp-1"
                title={payload?.fileData?.name}
              >
                {payload?.fileData?.name}
              </p>
              <div className="rista-sprite ic-pencil-blue"></div>
            </div>
          ) : (
            <p className="text-16 text-blue font-semibold m-0">+ Choose File</p>
          )}
          <input
            style={{ display: "none" }}
            type="file"
            id={"uploadImage"}
            multiple={false}
            className="upload-file-input"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(e: any) =>
              handleInputChange(e.target.files[0], "fileData")
            }
          />
        </label>
        <p className="text-12 text-black/40">
          Files supported: CSV, Xlsx | Files Size: less than 10MB
        </p>
      </>
    );
  }

  function renderSendTemplate(): React.JSX.Element {
    let enableCta =
      payload?.dateRange?.startDate && payload?.dateRange?.endDate;
    return (
      <div className="max-h-[90%] overflow-auto pt-10 sm:pt-20">
        <div className="modal-header flex items-center justify-between">
          <p className="text-22 font-semibold">Download Expense Data</p>
          <div
            className="rista-sprite ic-cross-black"
            onClick={onModalClose}
          ></div>
        </div>
        {renderCustomDropDown(DROPDOWN_CONFIGS.branchIds)}
        <div className="mb-32">
          <p className="text-14 text-[#656565] mb-8">
            Date Range <span className="text-[#9A3131]">*</span>
          </p>
          <DateRangePicker
            dateFormat={DATE_TIME_FORMATS.dayMonthYear}
            disabledDate={disabledDate}
            timeDifference={90}
            errorMessage={`You can select date range max upto 90 days!`}
            onSubmit={onDateRangeSubmission}
            defaultDateRange={[null, null]}
            customClass="!w-full"
          ></DateRangePicker>
        </div>
        <div className="modal-footer">
          <CtaComponent
            ctaSize={CTA_SIZE.large}
            ctaText={"Export"}
            ctaType={CTA_TYPE.active_cta}
            onCtaClick={handleSendTemplate}
            customClass={"w-full"}
            isDisabled={!enableCta}
          ></CtaComponent>
        </div>
      </div>
    );
  }

  function renderUploadSection(): React.JSX.Element {
    const enableUploadCta =
      payload?.dateRange?.startDate &&
      payload?.dateRange?.endDate &&
      payload?.fileData;
    return (
      <div className="max-h-[90%] overflow-auto pt-10">
        <div className={`modal-header flex items-center`}>
          <div
            className="rista-sprite ic-arrow-left"
            onClick={() => setOpenUploadSection(false)}
          ></div>
          <p className="text-22 font-semibold">Expense Upload - Upload File</p>
        </div>
        {renderFileChooser()}
        <div className="mb-32 mt-20">
          <p className="text-14 text-[#656565] mb-8">
            Date Range <span className="text-[#9A3131]">*</span>
          </p>
          <DateRangePicker
            dateFormat={DATE_TIME_FORMATS.dayMonthYear}
            disabledDate={disabledDate}
            timeDifference={90}
            errorMessage={`You can select date range max upto 90 days!`}
            onSubmit={onDateRangeSubmission}
            defaultDateRange={[null, null]}
            customClass="!w-full"
          ></DateRangePicker>
        </div>
        <div className="modal-footer py-16">
          <CtaComponent
            ctaSize={CTA_SIZE.medium}
            ctaText="Upload File"
            ctaType={CTA_TYPE.active_cta}
            onCtaClick={onUploadFileCtaClick}
            customClass={"w-full"}
            isDisabled={!enableUploadCta}
          ></CtaComponent>
        </div>
      </div>
    );
  }

  function renderOptions() {
    return (
      <>
        <div className="modal-header flex justify-between items-center">
          <p className="text-22 font-semibold">Expense Data Upload</p>
          <div
            className="rista-sprite ic-cross-black"
            onClick={onModalClose}
          ></div>
        </div>
        <div
          className="border-[#DDDDDD] border-[1px] rounded-[12px] p-20 bg-white flex items-center mb-16 cursor-pointer mt-10"
          onClick={() => {
            setOpenUploadSection(true);
            setPayload(INITIAL_STATE_UPLOAD_DATA);
          }}
        >
          <div className="rista-sprite ic-upload-image"></div>
          <div className="flex flex-col ml-16">
            <p className="text-16 font-semibold mb-4">Upload File</p>
            <p className="text-13 font-normal">
              File format: CSV | File Size: Less than 5MB
            </p>
          </div>
        </div>
        <div
          className="border-[#DDDDDD] border-[1px] rounded-[12px] p-20 bg-white flex items-center cursor-pointer"
          onClick={handleSendTemplate}
        >
          <div className="rista-sprite ic-email"></div>
          <div className="flex flex-col ml-16">
            <p className="text-16 font-semibold mb-4">Email Template</p>
            <p className="text-13 font-normal">
              Template will be sent to your registered Email-id: {userEmail}
            </p>
          </div>
        </div>
      </>
    );
  }

  function renderInstruction(){
    return (
      <div className="">
        <Accordion
          TransitionProps={{ unmountOnExit: true }}
          expanded={isExpend}
          onChange={() => setIsExpend(prev => !prev)}
          className={`bg-[#0000001A] mt-[20px] h-[20h]`}
        >
          <AccordionSummary
            className="w-[100%] px-[20px]"
          >
            <div className="heading">
              <div>{''}</div>
              <div className="font-bold">{'Instructions'}</div>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div style = {{maxHeight: `${window.outerHeight / 7}px`}} className={`sm:max-h-[20vh] overflow-y-scroll`}>
              {
                UPLOAD_INSTRUCTION?.map((val: any) => {
                  return (
                    <div>
                      <div className="heading pb-[20px]">
                        <div className="text-[16px] font-bold pb-[10px]">{val.heading}</div>
                        {Array.isArray(val.description)
                          ? <div>{
                            val.description.map((value: any) => {
                              return (
                              <div className="pb-[10px] text-[14px] font-[400]">{value}</div>
                              )
                            })
                          }</div>
                          : <div className="text-[14px] font-[400]">{val.description}</div>}
                      </div>
                    </div>
                  )
                })
              }
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    )
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <Box
        className={`recon-file-upload-modal modal-center w-[60%] h-[70%] sm:h-[85%] py-64 px-20
          sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
          sm:w-full
        `}
      >
        {openUploadSection ? renderUploadSection() : null}
        {openUploadSection || isModeDownload ? null : renderOptions()}
        {isModeDownload ? renderSendTemplate() : null}
        <div className="">
        {!(openUploadSection || isModeDownload) && renderInstruction()}
        </div>
      </Box>
    </Modal>
  );
};

export default ExpenseUploadModal;
